/**
 * useAccessibilitySettings — Convenience hook for AccessibilityContext.
 *
 * Returns the current accessibility settings (theme, fontScale,
 * animationsEnabled, highContrast, lineSpacing, cvdMode, fontFamily,
 * fontSize) plus setter helpers. Throws if used outside an
 * AccessibilityProvider.
 *
 * @module hooks/useAccessibilitySettings
 * @see TASK-WIDGET-ACCESSIBILITY-CONTEXT
 */

import { useContext } from 'react';
import {
  AccessibilityContext,
  type AccessibilityContextValue,
  type AccessibilitySettings,
  type AccessibilityTheme,
  type CvdMode,
  type AccessibilityFontFamily,
  type LineSpacing,
} from '../contexts/AccessibilityContext';

export type {
  AccessibilityContextValue,
  AccessibilitySettings,
  AccessibilityTheme,
  CvdMode,
  AccessibilityFontFamily,
  LineSpacing,
};

export const useAccessibilitySettings = (): AccessibilityContextValue => {
  const ctx = useContext(AccessibilityContext);
  if (ctx === undefined) {
    throw new Error(
      'useAccessibilitySettings must be used within an AccessibilityProvider',
    );
  }
  return ctx;
};

export default useAccessibilitySettings;
